import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { PresenceInput } from '../../components/ui/presence-input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import OrganisationLookup from '../../components/ui/organisation-lookup';
import QuickCreateOrganisation from '../../components/dialogs/QuickCreateOrganisation';
import RelationshipSummary from '../../components/graphs/RelationshipSummary';
import { dataApi, getCustomerOrganisationLinks, createCustomerOrganisationLink, deleteCustomerOrganisationLink, getCustomerRelationships } from '../../services/api';
import { useCustomerCollaboration } from '../../hooks/useCustomerCollaboration';
import RecordHistoryButton from '../../components/history/RecordHistoryButton';
import CollaborationIndicator from '../../components/ui/collaboration-indicator';
import { Phone, Mail, ChevronRight, Building2, Trash2, Users, BarChart3, List } from 'lucide-react';

const CustomerForm = ({ customerId, isOpen, onClose }) => {
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [regions, setRegions] = useState([]);
  const [orgLinks, setOrgLinks] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateName, setQuickCreateName] = useState('');
  const [loading, setLoading] = useState(false);
  const [relationshipView, setRelationshipView] = useState('list');
  // setSaving removed - collaboration hook autosaves
  const [error, setError] = useState(null);

  // Use collaboration hook - manages customer data and realtime sync
  const {
    data: customer,
    activeUsers,
    currentUser,
    isOnline,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = useCustomerCollaboration(customerId, {});

  const loadCustomerData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [quoteRequestsData, links, rels] = await Promise.all([
        dataApi.customerQuoteRequests(customerId),
        getCustomerOrganisationLinks(customerId).catch(() => []),
        getCustomerRelationships(customerId).catch(() => [])
      ]);
      setQuoteRequests(quoteRequestsData || []);
      setOrgLinks(links || []);
      setRelationships(rels || []);
    } catch (err) {
      setError('Failed to load customer data');
      console.error('Error loading customer data:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const loadRegions = async () => {
    try {
      const regionsData = await dataApi.regionList();
      setRegions(regionsData || []);
    } catch (err) {
      console.error('Error loading regions:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRegions();
      if (customerId) {
        loadCustomerData();
      } else {
        // No customer record exists yet - collaboration hook handles initial data
        setQuoteRequests([]);
      }
    }
  }, [isOpen, customerId, loadCustomerData]);

  const handleInputChange = (field, value) => {
    // Update through collaboration hook for realtime sync
    updateField(field, value);
  };

  // Field editing tracking for presence
  const handleInputFocus = (field) => {
    trackFieldEdit(field);
  };

  const handleInputBlur = () => {
    clearFieldEdit();
  };

  const handleOrganisationSelect = async (organisation) => {
    if (!organisation || !customerId) return;
    try {
      await createCustomerOrganisationLink({
        customerId,
        customerOrganisationId: organisation.$id
      });
      // Reload links
      const links = await getCustomerOrganisationLinks(customerId);
      setOrgLinks(links || []);
    } catch (err) {
      console.error('Failed to link organisation:', err);
    }
  };

  const handleRemoveOrgLink = async (linkId) => {
    try {
      await deleteCustomerOrganisationLink(linkId);
      setOrgLinks((prev) => prev.filter((l) => l.$id !== linkId));
    } catch (err) {
      console.error('Failed to remove org link:', err);
    }
  };

  const handleCreateOrganisation = (searchTerm) => {
    setQuickCreateName(searchTerm || '');
    setQuickCreateOpen(true);
  };

  const handleOrganisationCreated = async (newOrg) => {
    // Auto-link the newly created org to this customer
    if (customerId && newOrg?.$id) {
      try {
        await createCustomerOrganisationLink({
          customerId,
          customerOrganisationId: newOrg.$id
        });
        const links = await getCustomerOrganisationLinks(customerId);
        setOrgLinks(links || []);
      } catch (err) {
        console.error('Failed to auto-link new organisation:', err);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatRequestNumber = (quote) => {
    const raw = quote?.quoteRequestNumber || quote?.quote_request_number || quote?.requestNumber || quote?.request_number || quote?.$sequence;
    const numeric = parseInt(raw, 10);
    const padded = Number.isFinite(numeric) ? String(Math.max(0, numeric)).padStart(5, '0') : String(raw || '0').padStart(5, '0');
    return `QR-${padded}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-green-600';
      case 'in_progress': return 'text-yellow-600';
      case 'pending': return 'text-blue-600';
      case 'cancelled': return 'text-red-600';
      default: return 'text-gray-500';
    }
  };

  // Handle case where no customer ID provided
  if (!customerId) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="dialog-medium">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
            <DialogDescription>Customer information display</DialogDescription>
          </DialogHeader>
          <div className="text-center py-8">
            <p>This quote was created before the customer management system was implemented.</p>
            <p>Customer information is available in the quote request details above.</p>
            <p>To manage customers separately, please create a new quote through the customer app.</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="dialog-content-large max-w-5xl w-[1100px] max-h-[90vh] overflow-y-auto animate-none">
          <DialogHeader>
            <DialogTitle>Loading Customer...</DialogTitle>
            <DialogDescription>Loading customer information</DialogDescription>
          </DialogHeader>
          
          {/* Personal Details Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Organisation Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Organisation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>

          {/* Address Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Address Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>

          {/* Quote Requests Section Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Related Quote Requests</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="dialog-small">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>An error occurred while loading customer data</DialogDescription>
          </DialogHeader>
          <div className="text-center py-8 text-red-600">
            {error}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[1100px] max-h-[90vh] flex flex-col animate-none">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle>Customer: {customer?.firstName || customer?.first_name || ''} {customer?.lastName || customer?.last_name || ''}</DialogTitle>
              <CollaborationIndicator 
                isOnline={isOnline}
                activeUsers={activeUsers}
                currentUserId={currentUser?.id}
                align="left"
              />
              <RecordHistoryButton rowId={customerId} title="Customer" />
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {/* Personal Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <PresenceInput
                    id="firstName"
                    className="input-borderless"
                    value={customer?.firstName || customer?.first_name || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    onFocus={() => handleInputFocus('firstName')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('firstName')}
                    isCurrentUser={isCurrentUserEditing('firstName')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <PresenceInput
                    id="lastName"
                    className="input-borderless"
                    value={customer?.lastName || customer?.last_name || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    onFocus={() => handleInputFocus('lastName')}
                    onBlur={handleInputBlur}
                    editor={getFieldEditor('lastName')}
                    isCurrentUser={isCurrentUserEditing('lastName')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative group">
                    <PresenceInput
                      id="email"
                      type="email"
                      className="input-borderless pr-10"
                      value={customer?.email || ''}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onFocus={() => handleInputFocus('email')}
                      onBlur={handleInputBlur}
                      editor={getFieldEditor('email')}
                      isCurrentUser={isCurrentUserEditing('email')}
                    />
                    {customer?.email && customer.email.trim() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-8 w-8 opacity-0 group-hover:opacity-100"
                        onClick={() => window.open(`mailto:${customer.email}`)}
                        title="Email"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative group">
                    <PresenceInput
                      id="phone"
                      type="tel"
                      className="input-borderless pr-10"
                      value={customer?.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      onFocus={() => handleInputFocus('phone')}
                      onBlur={handleInputBlur}
                      editor={getFieldEditor('phone')}
                      isCurrentUser={isCurrentUserEditing('phone')}
                    />
                    {customer?.phone && customer.phone.toString().trim() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1 h-8 w-8 opacity-0 group-hover:opacity-100"
                        onClick={() => window.open(`tel:${customer.phone}`)}
                        title="Call"
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div></div>
              </div>
            </CardContent>
          </Card>

          {/* Relationships Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Relationships</CardTitle>
                <div className="flex items-center gap-1 border rounded-md p-0.5">
                  <button
                    type="button"
                    className={`p-1 rounded ${relationshipView === 'list' ? 'bg-muted' : 'hover:bg-muted/50'}`}
                    onClick={() => setRelationshipView('list')}
                    title="List view"
                  >
                    <List className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className={`p-1 rounded ${relationshipView === 'chart' ? 'bg-muted' : 'hover:bg-muted/50'}`}
                    onClick={() => setRelationshipView('chart')}
                    title="Chart view"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {relationshipView === 'chart' ? (
                customerId && (
                  <RelationshipSummary
                    entityId={customerId}
                    entityType="customer"
                  />
                )
              ) : (
                <>
                  {/* Organisations */}
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Organisations</Label>
                    {orgLinks.map((link) => (
                      <div key={link.$id} className="group/item flex items-center gap-2 p-1.5 rounded-md hover:bg-muted/30">
                        <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm font-medium flex-1 min-w-0 truncate">
                          {link.customerOrganisation?.name || 'Unknown'}
                        </span>
                        {link.role && (
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                            {link.role}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveOrgLink(link.$id)}
                          className="h-6 w-6 p-0 flex items-center justify-center text-slate-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity rounded"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {/* Lookup to add organisations */}
                    <OrganisationLookup
                      value={null}
                      onChange={handleOrganisationSelect}
                      onCreateNew={handleCreateOrganisation}
                      placeholder="Add organisation..."
                    />
                  </div>

                  {/* Related Contacts */}
                  {relationships.length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Related Contacts</Label>
                      {relationships.map((rel) => (
                        <div key={rel.$id} className="group/item flex items-center gap-2 p-1.5 rounded-md hover:bg-muted/30">
                          <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm font-medium flex-1 min-w-0 truncate">
                            {rel.relatedCustomer
                              ? `${rel.relatedCustomer.firstName || ''} ${rel.relatedCustomer.lastName || ''}`
                              : 'Unknown'}
                          </span>
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">
                            {rel.relationshipType}
                          </span>
                          {rel.description && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">{rel.description}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Address Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Address Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="street1">Street Address 1</Label>
                <PresenceInput
                  id="street1"
                  className="input-borderless"
                  value={customer?.street1 || ''}
                  onChange={(e) => handleInputChange('street1', e.target.value)}
                  onFocus={() => handleInputFocus('street1')}
                  onBlur={handleInputBlur}
                  editor={getFieldEditor('street1')}
                  isCurrentUser={isCurrentUserEditing('street1')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="street2">Street Address 2</Label>
                <PresenceInput
                  id="street2"
                  className="input-borderless"
                  value={customer?.street2 || ''}
                  onChange={(e) => handleInputChange('street2', e.target.value)}
                  onFocus={() => handleInputFocus('street2')}
                  onBlur={handleInputBlur}
                  editor={getFieldEditor('street2')}
                  isCurrentUser={isCurrentUserEditing('street2')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="suburb">Suburb</Label>
                <PresenceInput
                  id="suburb"
                  className="input-borderless"
                  value={customer?.suburb || ''}
                  onChange={(e) => handleInputChange('suburb', e.target.value)}
                  onFocus={() => handleInputFocus('suburb')}
                  onBlur={handleInputBlur}
                  editor={getFieldEditor('suburb')}
                  isCurrentUser={isCurrentUserEditing('suburb')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="region">Region</Label>
                <Select
                  value={customer?.region || undefined}
                  onValueChange={(value) => handleInputChange('region', value)}
                >
                  <SelectTrigger className="select-borderless">
                    <SelectValue placeholder="Select Region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions
                      .filter(region => region.region && region.region.trim())
                      .map((region, index) => (
                        <SelectItem key={`${region.region}-${index}`} value={region.region}>
                          {region.region}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode</Label>
                <PresenceInput
                  id="postcode"
                  className="input-borderless"
                  value={customer?.postcode || ''}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  onFocus={() => handleInputFocus('postcode')}
                  onBlur={handleInputBlur}
                  editor={getFieldEditor('postcode')}
                  isCurrentUser={isCurrentUserEditing('postcode')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Quote Requests Section */}
          <Card>
            <CardHeader>
              <CardTitle>Quote Requests ({quoteRequests.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quoteRequests.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  No quote requests found for this customer.
                </div>
              ) : (
                quoteRequests.map((quote) => {
                  const requestNumber = formatRequestNumber(quote);
                  return (
                    <div key={quote.id} className="flex items-center justify-between p-3 border rounded-md">
                      <div className="space-y-1">
                        <div className="text-sm font-semibold flex items-center gap-2">
                          <span>{requestNumber}</span>
                          <span className={`text-xs ${getStatusColor(quote.status)}`}>{quote.status || 'Unknown'}</span>
                        </div>
                        <div className="text-xs text-muted-foreground flex gap-3">
                          <span>Created: {formatDate(quote.created_at || quote.$createdAt)}</span>
                          <span>Updated: {formatDate(quote.updated_at || quote.$updatedAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => window.open(`/quote-requests/${quote.id || quote.$id}`, '_blank')}
                          aria-label="Open quote request"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick-create organisation dialog */}
        <QuickCreateOrganisation
          open={quickCreateOpen}
          onOpenChange={setQuickCreateOpen}
          initialName={quickCreateName}
          onCreated={handleOrganisationCreated}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CustomerForm;